/* eslint-disable no-underscore-dangle */
import { navigationClickEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@ag1/ui';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { HeaderNavigation, HeaderNavigationLink, NavigationLinkType } from 'types/studio/header';
import { HeaderFullV2, SanityKeyed } from 'types/studio/studio';
import { useDebounce } from 'use-debounce';
import HeaderMenuLink from '../LinkElement/LinkElement';
import styles from './NavigationItem.module.scss';

type SubmenuItemsType = Array<SanityKeyed<HeaderNavigationLink>>;

interface SubmenuItemsProps {
  opened: boolean;
  items: SubmenuItemsType;
  theme?: HeaderFullV2['theme'];
  onClick: (eventText?: string) => void;
}

const sendNavigationEvent = (clickText: string) =>
  navigationClickEvent({
    location: 'header_menu',
    navigationType: 'link',
    clickText
  });

const SubmenuItems = ({ items, opened, theme, onClick }: SubmenuItemsProps) => {
  const submenuWrapperRef = useRef<HTMLDivElement>(null);

  const handleCheckSize = () => {
    const wrapper = submenuWrapperRef.current;

    if (!wrapper) return;

    wrapper.style.setProperty('--max-height', `${wrapper.scrollHeight}px`);
  };

  const [checkHeight] = useDebounce(handleCheckSize, 400);

  useEffect(() => {
    handleCheckSize();

    window.addEventListener('resize', checkHeight, true);

    return () => {
      window.removeEventListener('resize', checkHeight);
    };
  }, [checkHeight, submenuWrapperRef]);

  return (
    <div
      className={clsx(styles.submenuWrapper, opened ? styles.opened : styles.closed)}
      ref={submenuWrapperRef}>
      {items.map(({ _key, type, navLink, navPage, text }) =>
        type === NavigationLinkType.page ? (
          <HeaderMenuLink
            key={_key}
            href={navPage?.slug?.current || '/'}
            className={clsx(styles.link, styles.submenuLink, getThemeCSSClass(theme))}
            onClick={() => onClick(text)}>
            {text}
          </HeaderMenuLink>
        ) : (
          <HeaderMenuLink
            key={_key}
            href={navLink || ''}
            className={clsx(styles.link, styles.submenuLink, getThemeCSSClass(theme))}
            onClick={() => onClick(text)}>
            {text}
          </HeaderMenuLink>
        )
      )}
    </div>
  );
};

type HeaderNavigationItem = HeaderNavigation | HeaderFullV2['shopLink'];

type Props = {
  theme: HeaderFullV2['theme'];
  onClick: () => void;
} & HeaderNavigationItem;

const NavigationItem = ({
  type,
  text,
  navLink = '',
  navPage,
  navSubmenu,
  theme,
  onClick
}: Props) => {
  const [openedSubmenu, openSubmenu] = useState<boolean>(false);

  const handleOpenSubmenu = () => {
    if (openedSubmenu) {
      openSubmenu(false);
    } else {
      openSubmenu(true);
    }
  };

  const handleClickItem = (eventText?: string) => {
    sendNavigationEvent(eventText || '');
    onClick();
  };

  let resolvedNavLink = navLink;
  if (type === NavigationLinkType.page) {
    resolvedNavLink = navPage?.slug?.current || '';
  }

  return (
    <div className={clsx(styles.linkWrapper, getThemeCSSClass(theme))} key={resolvedNavLink}>
      {(type === 'link' || type === 'page') && (
        <HeaderMenuLink
          href={resolvedNavLink}
          className={clsx(styles.link, getThemeCSSClass(theme))}
          onClick={() => handleClickItem(text)}>
          {text}
        </HeaderMenuLink>
      )}
      {type === 'submenu' && (
        <>
          <button
            className={clsx(
              styles.link,
              styles.expandedLink,
              openedSubmenu ? styles.opened : styles.closed,
              `theme_${theme}`
            )}
            style={{ background: 'none' }}
            type="button"
            onClick={handleOpenSubmenu}>
            {text}
            <ChevronDownIcon className={styles.expandIcon} />
          </button>

          {navSubmenu && (
            <SubmenuItems
              items={navSubmenu as SubmenuItemsType}
              opened={openedSubmenu}
              theme={theme}
              onClick={handleClickItem}
            />
          )}
        </>
      )}
    </div>
  );
};

export default NavigationItem;
